import { useEffect, useMemo } from "react";
import { Store } from "@tldraw/store";
import type { TLAssetStore, TLStore, TLStoreSnapshot } from "@tldraw/tlschema";
import { schema } from "../shared/schema";
import { CanvasEditor } from "./canvas/engine/editor";
import { CanvasView } from "./canvas/engine/view";
import {
  ArrowTool,
  DrawTool,
  EraserTool,
  GeoTool,
  HandTool,
  HighlightTool,
  NoteTool,
  TextTool,
} from "./canvas/engine/tools";
import { PlaceholderShapeUtil } from "./canvas/engine/builtin/placeholder";
import type { StateNodeConstructor } from "./canvas/engine/state-node";
import { SheetSelectTool } from "./sheet-select-tool";
import { isFrameLike, boardShapes } from "./board-shapes";
import { shapeVisibility } from "./board-rendering";
import { boardCameraOptions } from "./canvas-navigation";
import { controllers, customTools } from "./tools";
import { LibraryPlacementTool } from "./LibraryPlacementTool";
import { duplicateSelection } from "./sheet-operations";
import { selectContents } from "./sheet-selection";
import { notify } from "./api";
import { canvasFonts } from "./canvas-fonts";

// The board canvas on Planroom's own engine, with Planroom's measuring and
// drafting tools.

const tools = [
  SheetSelectTool,
  HandTool,
  TextTool,
  DrawTool,
  HighlightTool,
  EraserTool,
  GeoTool,
  NoteTool,
  ArrowTool,
  LibraryPlacementTool,
  ...customTools,
] as unknown as StateNodeConstructor[];

/** Planroom's board editor on its own engine; also used without a canvas for rendering. */
export function createBoardEditor(store: TLStore) {
  return new CanvasEditor({
    store,
    shapeUtils: boardShapes,
    tools,
    getShapeVisibility: shapeVisibility,
    isFrameLike,
    cameraOptions: boardCameraOptions,
    fallbackUtil: PlaceholderShapeUtil,
    fonts: canvasFonts,
    shortcuts: {
      "mod+d": (ed) => duplicateSelection(ed),
      "mod+a": (ed) => selectContents(ed),
      "mod+k": (ed) => controllers.get(ed)?.askAgent(),
    },
    onUnknownTool: (id) =>
      notify(`The ${id} tool is not available in the new editor yet.`),
  });
}

/**
 * A read-only board editor for a saved snapshot, without a canvas or sync:
 * project thumbnails and the agent renderer export from it directly.
 */
export function createSnapshotEditor(snapshot: TLStoreSnapshot, assets: TLAssetStore) {
  const store = new Store({
    schema,
    props: { defaultName: "", assets, onMount: () => {} } as never,
  }) as unknown as TLStore;
  store.loadStoreSnapshot(snapshot);
  const editor = createBoardEditor(store);
  editor.updateInstanceState({ isReadonly: true });
  return editor;
}

export function BoardCanvas({
  store,
  onMount,
}: {
  store: TLStore;
  onMount(editor: CanvasEditor): (() => void) | void;
}) {
  const editor = useMemo(() => createBoardEditor(store), [store]);
  useEffect(() => {
    const dispose = onMount(editor);
    return () => {
      dispose?.();
      editor.dispose();
    };
    // onMount is the board's setup for this editor instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);
  return <CanvasView editor={editor} />;
}
